/**
 * Slack API service.
 * Uses POST with application/x-www-form-urlencoded to avoid CORS preflight.
 * Slack API accepts token in the body as form data, which makes it a "simple request"
 * that doesn't trigger CORS preflight OPTIONS check.
 */

const SLACK_API = 'https://slack.com/api'
const DEFAULT_TOKEN =
  'xoxb-10681962250992-10638476041735-08YCjQ8syLi7D3nXdzqcMf0C'
export function getSlackToken(): string {
  const token = localStorage.getItem('vsm_slack_token')
  if (!token) {
    localStorage.setItem('vsm_slack_token', DEFAULT_TOKEN)
    return DEFAULT_TOKEN
  }
  return token
}
export function setSlackToken(token: string): void {
  localStorage.setItem('vsm_slack_token', token)
}
export function isSlackConnected(): boolean {
  return !!getSlackToken()
}
export interface SlackChannel {
  id: string
  name: string
  is_channel: boolean
  is_group: boolean
  is_im: boolean
  is_private: boolean
  num_members?: number
  topic?: {
    value: string
  }
  purpose?: {
    value: string
  }
}
export interface SlackMessage {
  type: string
  subtype?: string
  ts: string
  user?: string
  text: string
  thread_ts?: string
  reply_count?: number
  reactions?: Array<{
    name: string
    count: number
    users: string[]
  }>
  bot_id?: string
  app_id?: string
  files?: Array<{
    name: string
    mimetype: string
    url_private: string
  }>
}
export interface SlackUser {
  id: string
  name: string
  real_name: string
  profile: {
    image_24: string
    image_32: string
    image_48: string
    image_72: string
    avatar_hash: string
    display_name: string
    status_text?: string
    status_emoji?: string
  }
  is_bot?: boolean
}

// Simple cache for users
const userCache = new Map<string, SlackUser>()

/**
 * Call Slack API using POST with form-urlencoded body.
 * This avoids CORS preflight because:
 * - Method is POST
 * - Content-Type is application/x-www-form-urlencoded
 * - No custom headers (token goes in body, not Authorization header)
 * These qualify as a "simple request" per CORS spec.
 */
async function fetchSlackAPI<T>(
  endpoint: string,
  params: Record<string, any> = {},
): Promise<T> {
  const token = getSlackToken()
  if (!token) {
    throw new Error('No hay token de Slack configurado.')
  }
  const url = `${SLACK_API}/${endpoint}`
  const startTime = performance.now()

  // Build form data with token included in body
  const formData = new URLSearchParams()
  formData.append('token', token)
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) {
      formData.append(k, String(v))
    }
  })
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
    })
    const durationMs = Math.round(performance.now() - startTime)
    if (!response.ok) {
      const err = `Slack API HTTP error: ${response.status}`
      logSlack(endpoint, params, null, false, err, durationMs)
      throw new Error(err)
    }
    const data = await response.json()
    if (!data.ok) {
      const errorMsg = data.error || 'Error desconocido de Slack'
      logSlack(endpoint, params, data, false, errorMsg, durationMs)
      throw new Error(errorMsg)
    }
    logSlack(endpoint, params, data, true, undefined, durationMs)
    return data as T
  } catch (err: any) {
    const durationMs = Math.round(performance.now() - startTime)
    if (!err.message?.includes('Slack API')) {
      logSlack(endpoint, params, null, false, err.message, durationMs)
    }
    throw err
  }
}
function logSlack(
  tool: string,
  params: any,
  data: any,
  success: boolean,
  error?: string,
  durationMs?: number,
) {
  try {
    import('../hooks/useDebugLogs').then(({ pushDebugLog }) => {
      pushDebugLog({
        source: 'slack',
        tool,
        params,
        rawResponse: data,
        processedData: data,
        success,
        error,
        durationMs,
      })
    })
  } catch {}
}
export const slack = {
  /**
   * Get list of channels (public and private)
   */
  async getChannels(): Promise<SlackChannel[]> {
    const data = await fetchSlackAPI<{
      channels: SlackChannel[]
    }>('conversations.list', {
      types: 'public_channel,private_channel',
      exclude_archived: true,
      limit: 100,
    })
    return data.channels || []
  },
  /**
   * Get messages from a specific channel
   */
  async getChannelHistory(
    channelId: string,
    limit: number = 50,
  ): Promise<SlackMessage[]> {
    const data = await fetchSlackAPI<{
      messages: SlackMessage[]
    }>('conversations.history', {
      channel: channelId,
      limit,
    })
    return data.messages || []
  },
  /**
   * Send a message to a channel.
   * Automatically converts Markdown formatting to Slack mrkdwn.
   */
  async sendMessage(channelId: string, text: string): Promise<SlackMessage> {
    const slackText = markdownToSlackMrkdwn(text)
    const data = await fetchSlackAPI<{
      message: SlackMessage
    }>('chat.postMessage', {
      channel: channelId,
      text: slackText,
    })
    return data.message
  },
  /**
   * Get user info by ID (with caching)
   */
  async getUserInfo(userId: string): Promise<SlackUser | null> {
    if (!userId) return null
    if (userCache.has(userId)) {
      return userCache.get(userId)!
    }
    try {
      const data = await fetchSlackAPI<{
        user: SlackUser
      }>('users.info', {
        user: userId,
      })
      if (data.user) {
        userCache.set(userId, data.user)
        return data.user
      }
      return null
    } catch (err) {
      console.error(`Error fetching user ${userId}:`, err)
      return null
    }
  },
  /**
   * Get all users in the workspace
   */
  async getUsers(): Promise<SlackUser[]> {
    const data = await fetchSlackAPI<{
      members: SlackUser[]
    }>('users.list', {
      limit: 200,
    })

    // Populate cache
    if (data.members) {
      data.members.forEach((user) => userCache.set(user.id, user))
    }
    return data.members || []
  },
  /**
   * Get channel info
   */
  async getChannelInfo(channelId: string): Promise<SlackChannel> {
    const data = await fetchSlackAPI<{
      channel: SlackChannel
    }>('conversations.info', {
      channel: channelId,
    })
    return data.channel
  },
  /**
   * Upload a file to a channel
   * Uses the newer files.getUploadURLExternal + files.completeUploadExternal flow
   * Falls back to legacy files.upload if needed
   */
  async uploadFile(channelId: string, file: File): Promise<any> {
    const token = getSlackToken()
    if (!token) throw new Error('No hay token de Slack configurado.')

    // Use legacy files.upload with FormData (simpler, works with bot tokens)
    const formData = new FormData()
    formData.append('token', token)
    formData.append('channels', channelId)
    formData.append('file', file)
    formData.append('filename', file.name)
    formData.append('title', file.name)
    const response = await fetch(`${SLACK_API}/files.upload`, {
      method: 'POST',
      body: formData,
    })
    if (!response.ok) {
      throw new Error(`Slack file upload HTTP error: ${response.status}`)
    }
    const data = await response.json()
    if (!data.ok) {
      throw new Error(data.error || 'Error al subir archivo a Slack')
    }
    return data.file
  },
}

/** Format Slack timestamp to Date */
export function formatSlackTimestamp(ts: string): Date {
  return new Date(parseFloat(ts) * 1000)
}

/**
 * Convert standard Markdown to Slack mrkdwn format.
 * Applied automatically before sending messages to Slack.
 *
 * Conversions:
 *   **bold**  → *bold*
 *   *italic*  → _italic_
 *   ~~strike~~ → ~strike~
 *   [text](url) → <url|text>
 *   ### Header → *Header*
 *   - item → • item
 *   `code` and ```blocks``` are kept as-is (Slack supports them natively).
 */
export function markdownToSlackMrkdwn(text: string): string {
  if (!text) return ''
  let result = text

  // 1. Protect code blocks and inline code from transformation
  const codeBlocks: string[] = []
  result = result.replace(/```[\s\S]*?```/g, (match) => {
    codeBlocks.push(match)
    return `__CODEBLOCK_${codeBlocks.length - 1}__`
  })
  const inlineCodes: string[] = []
  result = result.replace(/`[^`]+`/g, (match) => {
    inlineCodes.push(match)
    return `__INLINECODE_${inlineCodes.length - 1}__`
  })

  // 2. Headers: # Title / ## Title / ### Title → *Title* (bold in Slack)
  result = result.replace(/^#{1,6}\s+(.+)$/gm, '*$1*')

  // 3. Links: [text](url) → <url|text>
  result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<$2|$1>')

  // 4. Bold: **text** → *text* (Slack uses single asterisk for bold)
  result = result.replace(/\*\*([^*]+)\*\*/g, '*$1*')

  // 5. Italic: *text* (single asterisk, not already converted bold) → _text_
  //    After step 4, remaining single *text* are italics
  //    Be careful: at this point bold is already *text*, so we need to
  //    identify original single-asterisk italics.
  //    Strategy: convert _text_ markdown italic to Slack _text_ (already correct)
  //    For *text* that wasn't **text**: these are now ambiguous with converted bold.
  //    Since we converted **bold** → *bold* first, any remaining original *italic*
  //    would conflict. Best approach: use _italic_ in source markdown.
  //    No additional conversion needed — _italic_ works in both formats.

  // 6. Strikethrough: ~~text~~ → ~text~
  result = result.replace(/~~([^~]+)~~/g, '~$1~')

  // 7. Bullet lists: - item or * item → • item (prettier in Slack)
  result = result.replace(/^(\s*)[-*]\s+/gm, '$1• ')

  // 8. Numbered lists: keep as-is (Slack renders them fine)

  // 9. Blockquotes: > text → > text (same in both, no change needed)

  // 10. Horizontal rules: --- or *** → ─── (Slack doesn't render HR)
  result = result.replace(/^[-*_]{3,}$/gm, '───────────────────')

  // Restore code blocks and inline code
  inlineCodes.forEach((code, i) => {
    result = result.replace(`__INLINECODE_${i}__`, code)
  })
  codeBlocks.forEach((block, i) => {
    result = result.replace(`__CODEBLOCK_${i}__`, block)
  })
  return result
}

/** Parse Slack mrkdwn text to readable text (plain) */
export function parseSlackMessage(text: string): string {
  if (!text) return ''
  let parsed = text

  // Replace user mentions <@U12345>
  parsed = parsed.replace(/<@([A-Z0-9]+)>/g, (match, userId) => {
    const user = userCache.get(userId)
    return user ? `@${user.profile?.display_name || user.name}` : match
  })

  // Replace channel mentions <#C12345|general>
  parsed = parsed.replace(/<#([A-Z0-9]+)\|([^>]+)>/g, '#$2')

  // Replace links <http://example.com|Example>
  parsed = parsed.replace(/<([^|>]+)\|([^>]+)>/g, '$2')

  // Replace simple links <http://example.com>
  parsed = parsed.replace(/<([^>]+)>/g, '$1')

  // Decode HTML entities
  parsed = parsed
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
  return parsed
}

/** Parse Slack mrkdwn to standard Markdown for rendering */
export function parseSlackToMarkdown(text: string): string {
  if (!text) return ''
  let parsed = text

  // Replace user mentions <@U12345>
  parsed = parsed.replace(/<@([A-Z0-9]+)>/g, (match, userId) => {
    const user = userCache.get(userId)
    return user ? `**@${user.profile?.display_name || user.name}**` : match
  })

  // Replace channel mentions <#C12345|general>
  parsed = parsed.replace(/<#([A-Z0-9]+)\|([^>]+)>/g, '**#$2**')

  // Replace links <http://example.com|Example>
  parsed = parsed.replace(/<([^|>]+)\|([^>]+)>/g, '[$2]($1)')

  // Replace simple links <http://example.com>
  parsed = parsed.replace(/<(https?:\/\/[^>]+)>/g, '[$1]($1)')

  // Decode HTML entities (before mrkdwn conversion)
  parsed = parsed
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')

  // Convert Slack mrkdwn to standard Markdown:
  // *bold* → **bold** (Slack uses single *, Markdown uses double **)
  // But be careful not to touch ** that are already markdown or code blocks
  // First protect code blocks
  const codeBlocks: string[] = []
  parsed = parsed.replace(/```[\s\S]*?```/g, (match) => {
    codeBlocks.push(match)
    return `__CODEBLOCK_${codeBlocks.length - 1}__`
  })
  const inlineCodes: string[] = []
  parsed = parsed.replace(/`[^`]+`/g, (match) => {
    inlineCodes.push(match)
    return `__INLINECODE_${inlineCodes.length - 1}__`
  })

  // Slack *bold* → Markdown **bold**
  // But skip if already **bold** (standard markdown)
  parsed = parsed.replace(
    /(?<!\w)(?<!\*)\*(?!\*)([^\s*](?:[^*]*[^\s*])?)\*(?!\*)(?!\w)/g,
    '**$1**',
  )

  // Slack _italic_ → Markdown *italic*
  parsed = parsed.replace(/(?<!\w)_([^\s_](?:[^_]*[^\s_])?)_(?!\w)/g, '*$1*')

  // Slack ~strikethrough~ → Markdown ~~strikethrough~~
  // But skip if already ~~strikethrough~~
  parsed = parsed.replace(
    /(?<!\w)(?<!~)~(?!~)([^\s~](?:[^~]*[^\s~])?)~(?!~)(?!\w)/g,
    '~~$1~~',
  )

  // Restore code blocks and inline code
  inlineCodes.forEach((code, i) => {
    parsed = parsed.replace(`__INLINECODE_${i}__`, code)
  })
  codeBlocks.forEach((block, i) => {
    parsed = parsed.replace(`__CODEBLOCK_${i}__`, block)
  })
  return parsed
}
